import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Loads categories (once, cached in module) and athletes-with-categories for
 * a specific season. Returns:
 *  - categories: list of { id, code, male_name, female_name, category_type }
 *  - catMap: { [athlete_id]: [{ category_id, category_code, display_name, is_primary }] }
 *  - filterAthletes(athletes, category_code): returns athletes whose season_categories
 *    contain a matching category_code (primary OR additional).
 */
let _catCache = null;

export function useSeasonCategories(seasonId) {
  const [categories, setCategories] = useState(_catCache || []);
  const [catMap, setCatMap] = useState({});
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const skip = user?.role === "athlete";  // categorie per atleta: endpoint dello staff (403)

  useEffect(() => {
    if (skip) { return undefined; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (!_catCache) {
          const c = await api.get("/categories");
          _catCache = c.data || [];
          if (!cancelled) setCategories(_catCache);
        }
        const url = seasonId ? `/athletes-with-categories?season_id=${seasonId}` : `/athletes-with-categories`;
        const { data } = await api.get(url);
        if (cancelled) return;
        const m = {};
        (data?.athletes || []).forEach((a) => { m[a.id] = a.season_categories || []; });
        setCatMap(m);
      } catch { if (!cancelled) setCatMap({}); /* ruolo senza accesso alle categorie: nessun filtro */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [seasonId, skip]);

  const filterByCategory = (list, code) => {
    if (!code || code === "all") return list;
    return list.filter((a) => (catMap[a.id] || []).some((c) => c.category_code === code));
  };

  return { categories, catMap, loading, filterByCategory };
}
